
import { useState } from 'react';
import { InventoryItem } from '../page';

interface StockAlertProps {
  items: InventoryItem[];
  onUpdateStock: (variantId: string, newQuantity: number) => void;
  onBack: () => void;
}

export default function StockAlert({ items, onUpdateStock, onBack }: StockAlertProps) {
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [newQuantity, setNewQuantity] = useState(0);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const startEditing = (itemId: string, currentQuantity: number) => {
    setEditingItem(itemId);
    setNewQuantity(currentQuantity);
  };

  const handleUpdateStock = (itemId: string) => {
    if (newQuantity >= 0) {
      onUpdateStock(itemId, newQuantity);
      setEditingItem(null);
    }
  };

  const outOfStockItems = items.filter(item => item.status === 'out_of_stock');
  const lowStockItems = items.filter(item => item.status === 'low_stock');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800 p-1"
          >
            <i className="ri-arrow-left-line text-xl sm:text-2xl"></i>
          </button>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Cảnh báo tồn kho</h2>
            <p className="text-sm text-gray-600 hidden sm:block">Sản phẩm cần nhập thêm hoặc đã hết hàng</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-close-circle-line text-red-600 text-lg sm:text-xl"></i>
              </div>
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-red-600 truncate">Hết hàng</p>
              <p className="text-lg sm:text-2xl font-bold text-red-900">{outOfStockItems.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-alarm-warning-line text-orange-600 text-lg sm:text-xl"></i>
              </div>
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-orange-600 truncate">Sắp hết</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-900">{lowStockItems.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Out of Stock Items */}
      {outOfStockItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-red-900 flex items-center">
              <i className="ri-close-circle-line mr-2 text-red-600"></i>
              Sản phẩm hết hàng ({outOfStockItems.length})
            </h3>
          </div>
          
          {/* Mobile Card View */}
          <div className="block sm:hidden divide-y divide-gray-200">
            {outOfStockItems.map((item) => (
              <div key={item.id} className="p-4">
                <div className="flex items-start space-x-3">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-16 h-16 object-cover object-center rounded-lg flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-image-line text-gray-400 text-xl"></i>
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{item.productName}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{item.category}</p>
                        <p className="text-xs text-gray-600 mt-1">{item.size} - {item.color}</p>
                      </div>
                      
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Hết hàng
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      {editingItem === item.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={newQuantity}
                            onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            min="0"
                            placeholder="Số lượng"
                          />
                          <button
                            onClick={() => handleUpdateStock(item.id)}
                            className="bg-green-600 text-white hover:bg-green-700 px-3 py-1 rounded text-xs font-medium"
                          >
                            Lưu
                          </button>
                          <button
                            onClick={() => setEditingItem(null)}
                            className="text-gray-600 hover:text-gray-700 px-2 py-1"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(item.id, item.quantity)}
                          className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Nhập hàng
                        </button>
                      )}
                      
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{formatPrice(item.unitPrice)}</p>
                        <p className="text-xs text-gray-500">Đơn giá</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-red-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider">
                    Sản phẩm
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider">
                    Size/Màu
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-red-700 uppercase tracking-wider">
                    Số lượng hiện tại
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-red-700 uppercase tracking-wider">
                    Đơn giá
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-red-700 uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {outOfStockItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.productName}
                            className="w-10 h-10 object-cover object-center rounded-lg mr-3"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
                            <i className="ri-image-line text-gray-400"></i>
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.productName}</div>
                          <div className="text-sm text-gray-500">{item.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.size}</div>
                      <div className="text-sm text-gray-500">{item.color}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-lg font-bold text-red-600">0</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatPrice(item.unitPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {editingItem === item.id ? (
                        <div className="flex items-center justify-center space-x-2">
                          <input
                            type="number"
                            value={newQuantity}
                            onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            min="0"
                            placeholder="Số lượng"
                          />
                          <button
                            onClick={() => handleUpdateStock(item.id)}
                            className="text-green-600 hover:text-green-700 p-1"
                          >
                            <i className="ri-check-line"></i>
                          </button>
                          <button
                            onClick={() => setEditingItem(null)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(item.id, item.quantity)}
                          className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          Nhập hàng
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Low Stock Items */}
      {lowStockItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-orange-900 flex items-center">
              <i className="ri-alarm-warning-line mr-2 text-orange-600"></i>
              Sản phẩm sắp hết ({lowStockItems.length})
            </h3>
          </div>
          
          {/* Mobile Card View */}
          <div className="block sm:hidden divide-y divide-gray-200">
            {lowStockItems.map((item) => (
              <div key={item.id} className="p-4">
                <div className="flex items-start space-x-3">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-16 h-16 object-cover object-center rounded-lg flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-image-line text-gray-400 text-xl"></i>
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">{item.productName}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{item.category}</p>
                        <p className="text-xs text-gray-600 mt-1">{item.size} - {item.color}</p>
                      </div>
                      
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                        Sắp hết
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg font-bold text-orange-600">{item.quantity}</span>
                        <span className="text-xs text-gray-500">còn lại</span>
                      </div>
                      
                      {editingItem === item.id ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={newQuantity}
                            onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            min="0"
                          />
                          <button
                            onClick={() => handleUpdateStock(item.id)}
                            className="bg-green-600 text-white hover:bg-green-700 px-3 py-1 rounded text-xs font-medium"
                          >
                            Lưu
                          </button>
                          <button
                            onClick={() => setEditingItem(null)}
                            className="text-gray-600 hover:text-gray-700 px-2 py-1"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(item.id, item.quantity)}
                          className="bg-orange-600 text-white hover:bg-orange-700 px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          <i className="ri-add-line mr-1"></i>
                          Nhập thêm
                        </button>
                      )}
                    </div>
                    
                    <div className="mt-2 text-right">
                      <p className="text-sm font-medium text-gray-900">{formatPrice(item.unitPrice)}</p>
                      <p className="text-xs text-gray-500">Đơn giá</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-orange-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-orange-700 uppercase tracking-wider">
                    Sản phẩm
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-orange-700 uppercase tracking-wider">
                    Size/Màu
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-orange-700 uppercase tracking-wider">
                    Số lượng còn lại
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-orange-700 uppercase tracking-wider">
                    Đơn giá
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-orange-700 uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lowStockItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.productName}
                            className="w-10 h-10 object-cover object-center rounded-lg mr-3"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
                            <i className="ri-image-line text-gray-400"></i>
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.productName}</div>
                          <div className="text-sm text-gray-500">{item.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{item.size}</div>
                      <div className="text-sm text-gray-500">{item.color}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="text-lg font-bold text-orange-600">{item.quantity}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatPrice(item.unitPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {editingItem === item.id ? (
                        <div className="flex items-center justify-center space-x-2">
                          <input
                            type="number"
                            value={newQuantity}
                            onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                            min="0"
                          />
                          <button
                            onClick={() => handleUpdateStock(item.id)}
                            className="text-green-600 hover:text-green-700 p-1"
                          >
                            <i className="ri-check-line"></i>
                          </button>
                          <button
                            onClick={() => setEditingItem(null)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(item.id, item.quantity)}
                          className="bg-orange-600 text-white hover:bg-orange-700 px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          Nhập thêm
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-green-600 text-2xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Tuyệt vời!</h3>
          <p className="text-gray-600 text-sm sm:text-base">Tất cả sản phẩm đều có đủ tồn kho</p>
        </div>
      )}
    </div>
  );
}
